import { WebSocketResponse } from "../schemas/webSocketResponseSchema";
import { WEBSOCKET_MESSAGE_TYPES } from "../constants/webSocketTypes";

export class WebSocketClient<T = any> {
    private ws: WebSocket | null = null;
    private url: string;
    private messageListeners: Array<(_msg: WebSocketResponse<T>) => void> = [];
    private onStatusCallback: ((status: 'connected' | 'disconnected' | 'connecting') => void) | null = null;
    private reconnectAttempts = 0;
    private reconnectTimeout: NodeJS.Timeout | null = null;
    private shouldReconnect = true;

    constructor(url: string) {
        this.url = url;
    }

    connect() {
        this.ws = new WebSocket(this.url);
        if (this.onStatusCallback) this.onStatusCallback('connecting');
        this.ws.onopen = () => {
            this.reconnectAttempts = 0;
            if (this.reconnectTimeout) {
                clearTimeout(this.reconnectTimeout);
                this.reconnectTimeout = null;
            }
            if (this.onStatusCallback) this.onStatusCallback('connected');
        };
        this.ws.onmessage = (event) => {
            const msg = JSON.parse(event.data);
            if ('type' in msg && msg.type === WEBSOCKET_MESSAGE_TYPES.PING) {
                this.send({ type: WEBSOCKET_MESSAGE_TYPES.PONG, unique_id: msg.unique_id, message: 'pong', status: 200 });
                // Do not pass ping to listeners
            } else if ('type' in msg && msg.type === WEBSOCKET_MESSAGE_TYPES.PONG) {
                // Do not pass pong to listeners
            }
            else {
                this.messageListeners.forEach(listener => listener(msg));
            }
        };
        this.ws.onclose = () => {
            if (this.onStatusCallback) this.onStatusCallback('disconnected');
            if (this.shouldReconnect) {
                this.reconnectAttempts += 1;
                const delay = Math.min(1000 * this.reconnectAttempts, 10000); // Exponential backoff capped at 10s
                if (this.onStatusCallback) this.onStatusCallback('connecting');
                this.reconnectTimeout = setTimeout(() => {
                    this.connect();
                }, delay);
            }
        };
    }

    send(msg: WebSocketResponse<any>) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(msg));
        }
    }

    onMessage(callback: (msg: WebSocketResponse<any>) => void) {
        this.messageListeners.push(callback);
    }

    offMessage(callback: (msg: WebSocketResponse<any>) => void) {
        this.messageListeners = this.messageListeners.filter(fn => fn !== callback);
    }

    onStatus(callback: (status: 'connected' | 'disconnected' | 'connecting') => void) {
        this.onStatusCallback = callback;
    }

    close() {
        this.shouldReconnect = false;
        if (this.ws) {
            this.ws.close();
        }
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        this.messageListeners = [];
    }
}
